import React from 'react';
import {observer} from 'mobx-react'
import {autorun} from 'mobx'
import EventEmitter from 'events'
import {getParentFromBinding, getAttributeNameFromBinding} from '../../utils/metadata-utils'
import {generateEventFunctions} from "../../utils/events/actionEventMapper";
import {getError} from '../../utils/error-utils';
import { BaseUIComponent, BaseUIComponentProps } from './BaseUIComponent';
import { EventProps, FlatDomainDataContext, FormBean, IDomainData, IUIBaseInputDef, IUIComponentDef, IUIRepeatableSectionDef } from '../../datatypes';
import {v4 as uuidv4} from 'uuid';

const metaDataWrapper = (UIComponent:typeof BaseUIComponent) => {

  class WrappedComponent extends React.Component<BaseUIComponentProps,{isShow:boolean}> {
    // todo: when clean up is done??? MaxListenersExceededWarning: Possible EventEmitter memory leak detected. 11 PARENT_TRIGGER_HIDE listeners added. Use emitter.setMaxListeners() to increase limit
    private triggers : {parentTriggerEvent?:EventEmitter}
    private isShowDisposer: any;

    constructor(props:BaseUIComponentProps) {
      super(props)
      this.triggers = {};
      if(this.hasParentTrigger()) {
        this.triggers.parentTriggerEvent = new EventEmitter();
        let emitterId = `emitter_${uuidv4()}`;
        // @ts-ignore
        this.triggers.parentTriggerEvent.__id = emitterId; 

        console.debug(`create parent trigger ${emitterId} ${this.props.metadata.parentQuestion} EventEmitter componentId=${props.metadata.componentId}`);
      }
      this.state = {
        isShow: true
      };
    }

    hasParentTrigger() {
      let parentQuestion = this.props.metadata.parentQuestion;
      return parentQuestion && parentQuestion != "";
    }

    checkIfEmpty(value:any):boolean {
      if (!isNaN(value) && value == 0) return true;
      else if (value == "") return true;
      else return !value;
    }

    async componentDidMount() {
      // trigger show/hide of the component based on parentQuestion and parentTrigger / hideEmpty
      this.isShowDisposer = autorun(() => {
        let newIsShow = true;
        let parentQuestion = this.props.metadata.parentQuestion;
        let parentTrigger = this.props.metadata.parentTrigger;// currently is a string, but assume it's in array format
        // @ts-ignore
        let hideEmpty = !!this.props.metadata.hideEmpty; // Input component

        // hide based on parent trigger and question
        if (this.hasParentTrigger()){
          parentTrigger = parentTrigger || '';
          let objectName = getParentFromBinding(parentQuestion);
          let objAttribute = getAttributeNameFromBinding(parentQuestion);
          let triggerObject = this.props.ctx[objectName!];
          let triggerTargetValues = parentTrigger.split(',');

          if (triggerObject == null) newIsShow = false;
          else if (triggerObject.data && Array.isArray(triggerObject.data)) {
            let triggers = triggerObject.data.filter(data => !!data[objAttribute!] && triggerTargetValues.includes(data[objAttribute!] as string));
            newIsShow = triggers && triggers.length > 0;
          }
          else {
            let currentValue = triggerObject.data[objAttribute!] as string;
            newIsShow = !!currentValue && triggerTargetValues.includes(currentValue);
          }
        }

        // hide based on component's hideEmpty 
        if (hideEmpty) {
          let binding = this.props.metadata.binding;
          let objectName = getParentFromBinding(binding);
          let objAttribute = getAttributeNameFromBinding(binding);
          let hideObject = this.props.ctx[objectName!];
          
          if (hideObject) { // TODO: what if hideObject.data is array???
            newIsShow = newIsShow && !(hideEmpty && this.checkIfEmpty((hideObject.data as IDomainData)[objAttribute!]));
          }          
        }

        if(newIsShow != this.state.isShow) {
          this.setState(() => ({
            isShow: newIsShow
          }));
          if(!newIsShow && this.hasParentTrigger()) {
            this.triggers.parentTriggerEvent!.emit('PARENT_TRIGGER_HIDE');
          }
        }
      });
    }

    componentWillUnmount() {
      this.isShowDisposer();
    }

    // TODO: check if needed - generate autorun for this as well to re-generate component
    generateInstructionProps(formBean:FormBean, binding:string|undefined, metadata:IUIComponentDef, otherProps:any) {
      let props = {} as {metadata?:IUIRepeatableSectionDef | IUIBaseInputDef};
      let newMetadata = {} as IUIRepeatableSectionDef | IUIBaseInputDef;
      let instructionList = formBean.InstructionList;
      let isVisible = this.state.isShow;

      if (instructionList && instructionList.length > 0) {
        for(let instruction of instructionList) {
          let domainObjectCheck = instruction.domainObject && binding && instruction.domainObject === getParentFromBinding(binding);
          let useDomainAttribute = instruction.domainAttribute ? true : false;
          let domainAttributeCheck = useDomainAttribute && binding && instruction.domainAttribute === getAttributeNameFromBinding(binding);
          let useDomainId = instruction.domainId ? true : false;
          let domainIdCheck = useDomainId && otherProps && otherProps.domainAttribute && otherProps[otherProps.domainAttribute.objectName] &&
            instruction.domainId === otherProps[otherProps.domainAttribute.objectName].Id;
          let isAssign = domainObjectCheck && domainAttributeCheck && (domainIdCheck || !useDomainId); // assign to specific attribute / domain id
          let isAssignToComponentId = (instruction.uicomponentId && metadata.componentId === instruction.uicomponentId); // assign to specific component

          // Only Repeatable component don't need DomainAttribute
          if (domainObjectCheck && !useDomainAttribute) {
            // change cardinality
            if (instruction.minCardinality || instruction.maxCardinality) {
              if (instruction.minCardinality === 1 && instruction.maxCardinality === 1 ){
                // If the cardinality is set to 1 then the repeatable component should render only a single section for the data of a repeatable component (TODO)
                // and remove the "Add Item" and "Delete" icons to disallow the user from adding or deleting the object.
                if ((metadata as IUIRepeatableSectionDef).addLabel) (newMetadata as IUIRepeatableSectionDef).addLabel = null;
                if ((metadata as IUIRepeatableSectionDef).addIcon) (newMetadata as IUIRepeatableSectionDef).addIcon = null;
                if ((metadata as IUIRepeatableSectionDef).deleteIcon) (newMetadata as IUIRepeatableSectionDef).deleteIcon = null;
                if ((metadata as IUIRepeatableSectionDef).maxItems) (newMetadata as IUIRepeatableSectionDef).maxItems = 1;
              }
            }
            if (instruction.selectionType) {
                (newMetadata as IUIRepeatableSectionDef).allowSelection = instruction.selectionType;
            }
            if(instruction.filter){
                (newMetadata as IUIRepeatableSectionDef).filter = instruction.filter;
            }
          }
          else if ( isAssign || isAssignToComponentId) {
              // todo: very error prone code !!!
              if ((metadata as IUIBaseInputDef).readonly != undefined) (newMetadata as IUIBaseInputDef).readonly = (instruction.readonly != null) ? instruction.readonly : (metadata as IUIBaseInputDef).readonly;
              if ((metadata as IUIBaseInputDef).disabled != undefined) (newMetadata as IUIBaseInputDef).disabled = (instruction.disabled != null) ? instruction.disabled : (metadata as IUIBaseInputDef).disabled;
              // todo: one hide is global ???
              if (instruction.hide) isVisible = false;
          }
        }
      }

      if(Object.keys(newMetadata).length > 0){
        //we can't modifiy the existing metadata state at the time of rendering
        // @ts-ignore
        props.metadata = {
          ...metadata,
          ...newMetadata
        }
      }

      return {instructionProps:props, isVisible};
    }
    
    generateEventProps() {
      let eventHandlerMap = {} as {[key:string]:string[]};
    //   @ts-ignore
      if (Array.isArray(this.props.metadata.onClick)) eventHandlerMap.onClick = this.props.metadata.onClick;
      // @ts-ignore
      if (Array.isArray(this.props.metadata.onChange)) eventHandlerMap.onChange = this.props.metadata.onChange;
      // @ts-ignore
      if (Array.isArray(this.props.metadata.onSelectionChange)) {
        // @ts-ignore
        eventHandlerMap.onSelectionChange = this.props.metadata.onSelectionChange;
      }

      let eventProps:EventProps = {};
      for(let eventName in eventHandlerMap) {
          let eventHandlers = generateEventFunctions(eventHandlerMap[eventName], eventName, this.props);
          if(eventHandlers){
            eventProps[eventName] = eventHandlers;      
          }
        // console.log(`gen event fn ${eventName}#${props[eventName].length} for ${typeof eventHandlerMap[eventName]} ${this.props.metadata.type} ${this.props.metadata.title}`);
      }

      return eventProps;
    }

    generateOtherPropsWithAttrBinding(formBean:FormBean, binding:string, ctx:FlatDomainDataContext,
                                      metadata:IUIComponentDef) {
        // TODO: error prone code!!!
        let objNames = binding.split('.');
        if(objNames.length != 2){
            // @ts-ignore
            console.error(`invalid attribute binding ${binding} type=${metadata.type} componentId=${metadata.componentId} listBinding=${metadata.listBinding}`);
        }
        let props = {} as {[key:string]:any};
        let objectName = objNames[0];
        let objAttributeName = objNames[1];

        if(!ctx[objectName]){
            console.error(`missing ${objectName} in domain data context`);
        }

        props[objectName] = ctx[objectName] ? ctx[objectName].data : {};
        let name= objAttributeName;

        let ctxObjMetaData = ctx[objectName] && ctx[objectName].metaData ? ctx[objectName].metaData : null;
        let metadataObjName = ctxObjMetaData && ctxObjMetaData!.attributes ? ctxObjMetaData.attributes.filter((e:{name:string})=>e.name==name) : null;
        let domainDataType = metadataObjName && metadataObjName[0] && metadataObjName[0].domainDataType ? metadataObjName[0].domainDataType : null;

        props.domainAttribute = {
            objectName: objectName,
            name: name,
            domainDataType: domainDataType,
            namePrefix: ctx.namePrefix ? ctx.namePrefix : ""
        };

        props.error = getError(props[objectName], objAttributeName, this.props.metadata);

        let singlePluginData = formBean.PluginData ? formBean.PluginData[this.props.metadata.type] : null;
        if(singlePluginData) {
            for(let property in singlePluginData){
            props[property] = singlePluginData[property];
            }
        }

        return props;
    }

    generateOtherPropsWithListBinding(formBean:FormBean, listBinding:string, ctx:FlatDomainDataContext) {
        // TODO: error prone code!!!
      let props = {} as {[key:string]:any};
      let objectName = getParentFromBinding(listBinding)!;
      let objNames = listBinding.split('.');
      let objAttribute = getAttributeNameFromBinding(listBinding)!;

      props[objectName] = ctx[objectName] ? ctx[objectName].data : {};
      let name= objAttribute == null ? objNames[objNames.length - 1] : objAttribute;

      let ctxObjMetaData = ctx[objectName] && ctx[objectName].metaData ? ctx[objectName].metaData : null;
      let metadataObjName = ctxObjMetaData && ctxObjMetaData!.attributes ? ctxObjMetaData.attributes.filter((e:{name:string})=>e.name==name) : null;
      let domainDataType = metadataObjName && metadataObjName[0] && metadataObjName[0].domainDataType ? metadataObjName[0].domainDataType : null;

      props.domainAttribute = {
        objectName: objectName,
        name: name,
        domainDataType: domainDataType,
        namePrefix: ctx.namePrefix ? ctx.namePrefix : ""
      };

      props.error = getError(props[objectName], objAttribute, this.props.metadata);

      let singlePluginData = formBean.PluginData ? formBean.PluginData[this.props.metadata.type] : null;
      if(singlePluginData) {
        for(let property in singlePluginData){
          props[property] = singlePluginData[property];
        }
      }

      return props;
    }

    generateOtherProps(formBean:any, ctx:any)  {
      let props = {
        domainAttribute: {
            namePrefix: ctx.namePrefix ? ctx.namePrefix : ""
          }
      };

      return props;
    }

    getCtx() {
      let ctx = this.props.ctx;
      if(this.triggers && Object.keys(this.triggers).length > 0) {
        ctx = {...ctx};//shallow copy
        let previousTriggers = ctx.triggers || {};
        //shallow copy triggers too otherwise object is stored by reference
        ctx.triggers = {...previousTriggers};
        for(let triggerName in this.triggers) {
            // @ts-ignore
          let ctxTrigger = ctx.triggers[triggerName];
            // @ts-ignore
            let newTrigger = this.triggers[triggerName];
          if(ctxTrigger) {
            //if there exist a similar trigger, reemit all the events
            // @ts-ignore
            ctxTrigger.eventNames().forEach(eventName => {
              ctxTrigger.on(eventName, () => {
            // @ts-ignore
            newTrigger.emit(eventName, ...arguments);
              })
            });
          }
            // @ts-ignore
            ctx.triggers[triggerName] = newTrigger;
        }
      }

      return ctx;
    }

    render() {
      let otherProps = {}; 
      let actionProps = {};

      let binding:string|undefined;
      if(this.props.metadata.binding){ 
          // qualified domain object attr name e.g "PaymentHeader.PaymentAmount"
        binding = this.props.metadata.binding;
        // AccountInput and PaymentMethod components highjack binding, e.g "PaymentHeader"
        if(this.props.metadata.type === 'AccountInput' || this.props.metadata.type === "PaymentMethod"){
            otherProps = this.generateOtherPropsWithListBinding(this.props.formBean, binding, this.props.ctx);
        }else{
            otherProps = this.generateOtherPropsWithAttrBinding(this.props.formBean, binding, this.props.ctx, this.props.metadata);
        }
      }else if((this.props.metadata as IUIRepeatableSectionDef).listBinding){
        binding = (this.props.metadata as IUIRepeatableSectionDef).listBinding;
        otherProps = this.generateOtherPropsWithListBinding(this.props.formBean, binding, this.props.ctx);
      }else{
        otherProps = this.generateOtherProps(this.props.formBean, this.props.ctx);
      }

      actionProps = this.generateEventProps();

      const {instructionProps, isVisible} = this.generateInstructionProps(this.props.formBean, binding, this.props.metadata, otherProps);
      
      const ctx = this.getCtx();
      const uiComponentProps = {...this.props,...otherProps,...actionProps,...instructionProps, ctx}
      // @ts-ignore avoid Warning: Encountered two children with the same key, `null`
      if(!uiComponentProps.key){ delete uiComponentProps.key; }
    //   console.debug(`WrappedComponent componentId:${componentId} type:${type}. binding:${binding} otherProps:${Object.keys(otherProps)} `+
    //                 ` actionProps:${Object.keys(actionProps)} instructionProps:${Object.keys(instructionProps)}`+
    //                 ` ctx keys:${Object.keys(ctx)}`);
      return (
        this.state.isShow && isVisible ?
        <UIComponent
          {...uiComponentProps}
        /> 
        : null
      );
    }
  }
    
  return observer(WrappedComponent); // check if this also need to be an observer
};

export default metaDataWrapper;
